import React, { useState, useEffect, useCallback, useRef } from 'react';
import AlgorithmCard from '../components/AlgorithmCard';
import { useServerConfig } from '../context/ServerConfigContext';
import './AlgorithmPage.css';

const AlgorithmPage = ({ type }) => {
  const { getApiUrl } = useServerConfig();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [dateList, setDateList] = useState([]);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const observer = useRef();

  // 마지막 요소를 관찰하는 ref callback
  const lastCardElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentDateIndex(prevIndex => prevIndex + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // 날짜 목록을 가져오는 함수
  const fetchDateList = useCallback(async () => {
    try {
      console.log('🔍 [AlgorithmPage] fetchDateList 호출됨');
      const rankApiUrl = getApiUrl('stock-api-server', '/ranks');
      console.log('🔗 [AlgorithmPage] rankApiUrl:', rankApiUrl);

      const response = await fetch(rankApiUrl);
      const result = await response.json();
      console.log('📥 [AlgorithmPage] ranks API 응답:', result);

      // ranks API는 직접 배열을 반환
      if (Array.isArray(result) && result.length > 0) {
        const dates = result.sort((a, b) => new Date(b) - new Date(a)); // 최신순 정렬
        setDateList(dates);
        console.log('📅 [AlgorithmPage] 날짜 목록 설정:', dates);
      } else if (result.success && result.data) {
        // 다른 형식의 응답도 처리
        const dates = result.data.sort((a, b) => new Date(b) - new Date(a));
        setDateList(dates);
        console.log('📅 [AlgorithmPage] 날짜 목록 설정:', dates);
      } else {
        console.warn('⚠️ [AlgorithmPage] ranks API 응답 데이터 없음:', result);
      }
    } catch (error) {
      console.error('❌ [AlgorithmPage] 날짜 목록 로드 실패:', error);
    }
  }, [getApiUrl]);

  // 분석 결과를 가져오는 함수
  const fetchAnalysisData = useCallback(async (dateString, isLoadMore = false) => {
    console.log(`🚀 [AlgorithmPage] fetchAnalysisData 호출:`, {
      dateString,
      isLoadMore,
      loading,
      type
    });

    if (loading) {
      console.log('⏸️ [AlgorithmPage] 이미 로딩 중이므로 중단');
      return;
    }

    setLoading(true);
    console.log(`🔍 [AlgorithmPage] ${type} 분석 데이터 요청:`, dateString);

    try {
      const alertApiUrl = getApiUrl('alert-api-server', '/analysis/execute');
      console.log('🔗 [AlgorithmPage] alertApiUrl:', alertApiUrl);

      // 날짜를 YYYYMMDD 형식으로 변환
      const formattedDate = dateString.replace(/-/g, '');

      const requestBody = {
        analysis_type: type === 'pressed' ? 'nullim' : 'theme',
        analyzed_date: formattedDate,
        analyzed_hour: 15,
        analyzed_minute: 0,
        analyzed_second: 0,
        nullim_days: 3,
        send_telegram: false
      };

      console.log('📤 API 요청:', requestBody);

      const response = await fetch(alertApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success && result.data && result.data.results) {
        const analysisData = result.data.results.map(item => ({
          date: dateString,
          stockCode: item.stock_code,
          stockName: item.stock_name,
          price: item.price,
          diff: item.diff,
          rank: item.rank,
          nullim_days: item.nullim_days,
          message: item.message,
          telegram_sent: item.telegram_sent,
          is_duplicate: item.is_duplicate
        }));

        console.log(`✅ ${dateString} 분석 데이터 로드:`, analysisData.length, '개');

        if (isLoadMore) {
          setData(prevData => [...prevData, ...analysisData]);
        } else {
          setData(analysisData);
        }

        if (analysisData.length === 0) {
          setHasMore(false);
        }
      } else {
        console.warn('⚠️ 분석 데이터 없음:', result);
        if (!isLoadMore) {
          setData([]);
        }
      }
    } catch (error) {
      console.error('❌ 분석 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [type, getApiUrl]);

  // 초기 데이터 로드
  useEffect(() => {
    console.log('🚀 [AlgorithmPage] 초기 데이터 로드 useEffect 실행:', type);
    setData([]);
    setCurrentDateIndex(0);
    setHasMore(true);
    setExpandedCards(new Set());
    fetchDateList();
  }, [type]);

  // 날짜 목록 로드 후 첫 번째 날짜 데이터 로드
  useEffect(() => {
    console.log('🔄 [AlgorithmPage] useEffect - dateList 변경:', {
      dateListLength: dateList.length,
      currentDateIndex,
      firstDate: dateList[0]
    });

    if (dateList.length > 0 && currentDateIndex === 0) {
      console.log('✅ [AlgorithmPage] 첫 번째 날짜로 분석 데이터 요청:', dateList[0]);
      fetchAnalysisData(dateList[0], false);
    }
  }, [dateList]);

  // 날짜 인덱스 변경 시 추가 데이터 로드
  useEffect(() => {
    if (currentDateIndex > 0 && currentDateIndex < dateList.length) {
      fetchAnalysisData(dateList[currentDateIndex], true);
    } else if (currentDateIndex >= dateList.length) {
      setHasMore(false);
    }
  }, [currentDateIndex, dateList]);

  // 카드 확장/축소 핸들러
  const handleCardToggle = (cardId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  // 페이지 제목 결정
  const getPageTitle = () => {
    switch (type) {
      case 'pressed':
        return '알고리즘 추천 - 눌림';
      case 'theme':
        return '알고리즘 추천 - 테마';
      default:
        return '알고리즘 추천';
    }
  };

  // 날짜별로 데이터를 그룹화
  const groupedData = data.reduce((acc, item) => {
    const date = item.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedData).sort((a, b) => new Date(b) - new Date(a));

  return (
    <div className="algorithm-page">
      <div className="algorithm-header">
        <h1 className="algorithm-title">{getPageTitle()}</h1>
        <p className="algorithm-description">
          AI 알고리즘이 분석한 {type === 'pressed' ? '눌림 패턴' : '테마 동향'} 기반 추천 종목입니다.
        </p>
      </div>

      <div className="algorithm-content">
        {sortedDates.map((date, dateIndex) => {
          const isLastSection = dateIndex === sortedDates.length - 1;

          return (
            <div
              key={date}
              className="date-card"
              ref={isLastSection ? lastCardElementRef : null}
            >
              <div className="date-card-header" onClick={() => handleCardToggle(date)}>
                <div className="date-info">
                  <h2 className="date-title">{formatDate(date)}</h2>
                  <span className="analysis-badge">{type === 'pressed' ? '눌림 분석' : '테마 분석'}</span>
                </div>
                <div className="date-summary">
                  <span className="stock-count">{groupedData[date].length}개 종목 발견</span>
                  <div className="expand-icon">
                    {expandedCards.has(date) ? '▼' : '▶'}
                  </div>
                </div>
              </div>

              {expandedCards.has(date) && (
                <div className="date-card-content">
                  <div className="stocks-list">
                    {groupedData[date].map((item, itemIndex) => (
                      <div key={`${item.date}-${item.stockCode}`} className="stock-item">
                        <div className="stock-info">
                          <h4 className="stock-name">{item.stockName}</h4>
                          <span className="stock-code">({item.stockCode})</span>
                        </div>

                        <div className="stock-details">
                          <div className="price-info">
                            <span className="stock-price">{item.price.toLocaleString()}원</span>
                            <span className={`change-rate ${item.diff > 0 ? 'positive' : item.diff < 0 ? 'negative' : 'neutral'}`}>
                              {item.diff > 0 ? '+' : ''}{item.diff.toFixed(2)}%
                            </span>
                          </div>

                          <div className="analysis-info">
                            <span className="rank">순위: {item.rank}위</span>
                            {item.nullim_days && (
                              <span className="nullim-days">눌림: {item.nullim_days}일</span>
                            )}
                          </div>
                        </div>

                        {item.message && (
                          <div className="message">
                            <span className="message-text">{item.message}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="loading-section">
            <div className="loading-spinner">⏳</div>
            <p>{currentDateIndex === 0 ? '분석 데이터를 로드하는 중...' : '과거 데이터를 로드하는 중...'}</p>
            <small>분석 작업은 최대 30초 정도 소요될 수 있습니다.</small>
          </div>
        )}

        {!hasMore && data.length > 0 && (
          <div className="end-message">
            <p>모든 데이터를 로드했습니다.</p>
          </div>
        )}

        {data.length === 0 && !loading && (
          <div className="empty-message">
            <p>추천 종목이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// 날짜 포맷팅 함수 (YYYYMMDD 형식 처리)
const formatDate = (dateString) => {
  // YYYYMMDD 형식을 YYYY-MM-DD로 변환
  let formattedDateString = dateString;
  if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
    formattedDateString = `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`;
  }

  const date = new Date(formattedDateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  // 날짜가 유효하지 않으면 원본 문자열 반환
  if (isNaN(date.getTime())) {
    return dateString;
  }

  if (date.toDateString() === today.toDateString()) {
    return '오늘';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return '어제';
  } else {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  }
};


export default AlgorithmPage;